/**
 * Lightweight dialog to log a Water Solution service.
 *
 * Not a wash, not a repair. Captures tank + PO + date + remarks, applies
 * the per-customer flat fee from ``water_solution_tariffs``, and lands on
 * its own monthly invoice (not mixed with wash invoices).
 *
 * The carrier dropdown defaults to STOLT but can be any active carrier
 * partner — so if ITT (or another carrier) asks for the same simple
 * service later, all we need is their tariff row and they can start
 * logging. Water Solution is a carrier-side service, so customer-only
 * partners are intentionally excluded from this picker.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Droplets, AlertCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function WaterSolutionDialog({ open, onClose, onLogged }) {
  const [customer, setCustomer] = useState("STOLT");
  const [tank, setTank] = useState("");
  const [po, setPo] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState("");
  const [tariff, setTariff] = useState(null);
  const [tariffMissing, setTariffMissing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    api.get("/partners?active=true&role=carrier")
      .then(({ data }) => { if (alive) setPartners(Array.isArray(data) ? data : (data?.items || [])); })
      .catch(() => {});
    return () => { alive = false; };
  }, [open]);

  // Pull the tariff for the picked customer so operators see the fee
  // before saving. Also surfaces "no tariff" early, so they can go set
  // one rather than discovering it on save.
  useEffect(() => {
    if (!open || !customer) { setTariff(null); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/water-solutions/tariffs");
        const t = (data || []).find((x) => x.customer_code === customer && x.active !== false);
        if (!alive) return;
        setTariff(t || null);
        setTariffMissing(!t);
      } catch {
        if (alive) { setTariff(null); setTariffMissing(true); }
      }
    })();
    return () => { alive = false; };
  }, [open, customer]);

  // Reset form fields each time the dialog re-opens so stale data from
  // a previous entry doesn't bleed into the next one.
  useEffect(() => {
    if (open) {
      setTank("");
      setPo("");
      setDate(new Date().toISOString().slice(0, 10));
      setRemarks("");
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!customer || !tank.trim() || !po.trim() || !date) {
      toast.error("All fields except remarks are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/water-solutions", {
        customer_code: customer,
        tank_number: tank.trim().toUpperCase(),
        po_number: po.trim(),
        service_date: date,
        remarks: remarks.trim() || undefined,
      });
      const sym = tariff?.currency === "EUR" ? "€" : "$";
      toast.success(`Water Solution logged — ${sym}${tariff?.flat_fee?.toFixed(2) || "0.00"} to ${customer}.`);
      onLogged?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="water-solution-dialog">
      <div className="bg-white rounded-md shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Droplets className="w-5 h-5 text-[#006E9E]" />
            <h2 className="font-bold text-lg tracking-tight">Log Water Solution</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Carrier *</label>
            <select
              required
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
              className="ps-input"
              data-testid="water-sol-customer"
            >
              {partners.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.code}{p.legal_name && p.legal_name !== p.code ? ` · ${p.legal_name}` : ""}
                </option>
              ))}
            </select>
            {tariff && (
              <div className="text-[11px] text-emerald-700 mt-1">
                Flat fee: <b className="font-mono">
                  {tariff.currency === "EUR" ? "€" : "$"}{Number(tariff.flat_fee).toFixed(2)}
                </b> per service.
              </div>
            )}
            {tariffMissing && (
              <div className="text-[11px] text-amber-800 mt-1 flex items-start gap-1.5">
                <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
                <span>
                  No Water Solution tariff for {customer}. Set one up in Wash → Tariffs before logging.
                </span>
              </div>
            )}
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Tank Number *</label>
            <input
              required
              value={tank}
              onChange={(e) => setTank(e.target.value.toUpperCase())}
              className="ps-input font-mono"
              placeholder="ABCD1234567"
              autoFocus
              data-testid="water-sol-tank"
            />
            <div className="text-[11px] text-gray-500 mt-1">
              Must currently be gated-IN under {customer}.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">PO Number *</label>
              <input
                required
                value={po}
                onChange={(e) => setPo(e.target.value)}
                className="ps-input font-mono"
                placeholder="STC-123456"
                data-testid="water-sol-po"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Service Date *</label>
              <input
                required
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="ps-input font-mono"
                data-testid="water-sol-date"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Remarks</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="ps-input"
              rows={2}
              placeholder="Optional notes"
              data-testid="water-sol-remarks"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button type="button" onClick={onClose} className="ps-btn ps-btn-secondary" disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || tariffMissing}
              className="ps-btn ps-btn-primary"
              data-testid="water-sol-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Droplets className="w-4 h-4" />}
              Log Water Solution
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
